import React, {useCallback, useEffect, useState} from "react";
import styled from "styled-components";
import ReactDataGrid from '@inovua/reactdatagrid-enterprise';
import '@inovua/reactdatagrid-enterprise/base.css';
import '../../assets/default-light.scss'
import {RowSpan, SaveExcelButton} from "../../assets/GlobalStyles";
import {i18n} from "./index";
import {exportCSV} from "../reports/ExportCSV";

function RowDetails (props) {
  const {entity, detailData} = props
  const [rowDetailsData, setRowDetailsData] = useState([])
  const [loading, setLoading] = useState(true)
  const [gridRef, setGridRef] = useState(null);
  useEffect(() => {
    let active = true
    setLoading(true)
    Promise.resolve(detailData?.(entity)).then(data => {
      if(active) {
        setRowDetailsData(Array.isArray(data) ? data : [])
        setLoading(false)
      }
    }).catch(() => {
      if(active) {
        setRowDetailsData([])
        setLoading(false)
      }
    })
    return () => { active = false }
  }, [detailData, entity]);
  /**
   * ...reference react data grid
   * auto size column size to fit
   */
  useEffect(() => {
    if(gridRef){
      gridRef.current.setColumnSizesToFit()
    }
  }, [gridRef])
  const renderRowContextMenu = (menuProps, { rowProps }) => {
    menuProps.autoDismiss = true
  }
  const emptyText = <p style={{
    fontSize: 16,
  }}>{'데이터가 없습니다.' }</p>
  return (
    <DetailPanel>
      <DetailPanelHeader>
        <DetailIdentity>
          <DetailConnector/>
          <div>
            <strong>지면별 상세 데이터</strong>
            <span><b>{entity?.siteName || "선택 매체"}</b>에 연결된 지면 성과입니다.</span>
          </div>
        </DetailIdentity>
        <DetailCount>{loading ? "조회 중" : `${rowDetailsData.length.toLocaleString()}개 지면`}</DetailCount>
      </DetailPanelHeader>
      <ReactDataGrid
        className="report-detail-grid"
        licenseKey={process.env.REACT_APP_DATA_GRID_LICENSE_KEY}
        handle={setGridRef}
        dataSource={rowDetailsData}
        columns={props.detailColumn}
        enableColumnAutosize={true}
        groups={props.detailGroups}
        showZebraRows={false}
        pagination={false}
        headerHeight={42}
        rowHeight={35}
        style={{height: 200}}
        emptyText={loading ? <p style={{fontSize:11}}>지면 데이터를 조회하고 있습니다.</p> : emptyText}
        activeCell={null}
        renderRowContextMenu={renderRowContextMenu}
        i18n={i18n}
      />
    </DetailPanel>
  )
}

const accountExpandHeight = 310
export default function TableDetail (props) {
  const {columns, data, groups, fileName } = props
  const [gridRef, setGridRef] = useState(null);
  const gridStyle = { minHeight: 450, ...props.style}

  useEffect(() => {
    if (props.downloadTrigger > 0 && gridRef) {
      exportCSV(gridRef, fileName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.downloadTrigger]);

  /**
   * ...빈 데이터 텍스트
   * @type {JSX.Element}
   */
  const emptyText = <p style={{
    fontSize: 16,
  }}>{props.emptyText !== undefined ? props.emptyText : '데이터가 없습니다.' }</p>

  /**
   * ...reference react data grid
   * auto size column size to fit
   */
  useEffect(() => {
    if(gridRef){
      gridRef.current.setColumnSizesToFit()
    }
  }, [gridRef])
  /**
   * 오른쪽 클릭 방지
   * @param menuProps
   * @param rowProps
   */
  const renderRowContextMenu = (menuProps, { rowProps }) => {
    menuProps.autoDismiss = true
  }

  /**
   * ...펼쳐보기
   * @param data
   * @returns {JSX.Element}
   */
  const renderContactsGrid = useCallback(({data}) => {
    return (
        <RowDetails
            entity={data}
            detailData={props.detailData}
            detailColumn={props.detailColumn}
            detailGroups={props.detailGroups}
        />
    );
  }, [props.detailColumn, props.detailData, props.detailGroups]);

  return(
    <>
      {/*엑셀다운 기능 임시 닫음*/}
      {props.downloadList && !props.externalDownload &&
        <RowSpan style={{justifyContent: 'flex-end'}}>
          <SaveExcelButton onClick={()=>exportCSV(gridRef, fileName)}>엑셀 저장</SaveExcelButton>
        </RowSpan>
      }
      <ReactDataGrid
        licenseKey={process.env.REACT_APP_DATA_GRID_LICENSE_KEY}
        handle={setGridRef}
        rowExpandHeight={accountExpandHeight}
        growExpandHeightWithDetails={false}
        rowHeights={null}
        headerHeight={48}
        renderDetailsGrid={renderContactsGrid}
        enableColumnAutosize={true}
        showColumnMenuLockOptions={false}
        showColumnMenuGroupOptions={false}
        emptyText={emptyText}
        idProperty={props.idProperty}
        dataSource={data}
        columns={columns}
        groups={groups}
        showZebraRows={false}
        pagination={props.pagination}
        livePagination={props.livePagination}
        scrollThreshold={props.scrollThreshold}
        // enableColumnHover={true}
        limit={30}
        style={Object.assign(gridStyle,props.style)}
        i18n={i18n}
        renderRowContextMenu={renderRowContextMenu}
        onRowClick={({data}, event) => {
          if (event?.target?.closest?.('button, a, [role="button"]')) return
          gridRef?.current?.toggleRowExpand?.(data)
        }}
        {...props}
      />
    </>
  )
}

const DetailPanel=styled.div`
  position:relative;height:270px;margin:10px 14px 10px 30px;padding:10px 12px 12px;
  border:1px solid #d7e3f1;border-radius:9px;background:var(--color-surface);
  box-shadow:0 4px 12px rgba(31,41,55,.05);
  &::before{position:absolute;top:-11px;left:24px;width:1px;height:11px;background:#b9cee5;content:"";}
`;
const DetailPanelHeader=styled.div`
  display:flex;height:37px;align-items:flex-start;justify-content:space-between;gap:12px;
`;
const DetailIdentity=styled.div`
  display:flex;align-items:flex-start;gap:8px;
  strong,span{display:block;}strong{color:#334155;font-size: var(--font-size-13);font-weight:750;}
  span{margin-top:3px;color:var(--color-text-muted);font-size: var(--font-size-11);}b{color:#3b6fa8;font-weight:700;}
`;
const DetailConnector=styled.i`
  width:8px;height:8px;margin-top:3px;border:2px solid var(--color-primary);border-radius:50%;background:var(--color-primary-soft);
`;
const DetailCount=styled.span`
  display:inline-flex;height:23px;align-items:center;padding:0 8px;border:1px solid #dbe7f5;
  border-radius:999px;background:#f4f8fd;color:#4a78aa;font-size: var(--font-size-11);font-weight:700;
`;
